import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  Plus, FolderKanban, MoreVertical, Trash2, GripVertical,
  CheckCircle2, Clock, Sparkles, X, ChevronDown, ChevronRight,
  Users, UserPlus, Shield, Edit2, AlertTriangle, LayoutGrid, Calendar,
  MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useProjectStore, useTaskStore, useTeamStore, useUIStore, useAdminStore, useAuthStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import TaskDetailDialog from '../components/TaskDetailDialog';
import GanttView from '../components/GanttView';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { projects, fetchProjects, createProject, deleteProject, setCurrentProject, currentProject, updateProject, fetchProjectMembers, addProjectMember, removeProjectMember, updateProjectStatus } = useProjectStore();
  const { tasks, fetchTasks, updateTask, deleteTask, reorderTask, addSubtask, updateSubtask, deleteSubtask } = useTaskStore();
  const { teams, fetchTeams } = useTeamStore();
  const { users, fetchUsers } = useAdminStore();
  const { user: currentUser } = useAuthStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);
  
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', team_id: null, is_business_critical: false });
  const [suggestedTasks, setSuggestedTasks] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [newSubtask, setNewSubtask] = useState({});
  
  // Project member management
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [projectMembers, setProjectMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('contributor');
  const [showEditProjectDialog, setShowEditProjectDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  
  // Inline user creation
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: '', email: '', password: '', is_admin: false });
  const { createUser } = useAdminStore();
  
  // Edit task
  const [showEditTaskDialog, setShowEditTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // View mode (kanban or gantt)
  const [viewMode, setViewMode] = useState('kanban');
  
  // Task detail dialog (for comments/attachments)
  const [showTaskDetailDialog, setShowTaskDetailDialog] = useState(false);
  const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);

  const handleOpenTaskDetail = (task) => {
    setSelectedTaskForDetail(task);
    setShowTaskDetailDialog(true);
  };

  const handleTaskDetailSave = async (updatedTask) => {
    try {
      await updateTask(updatedTask.id, updatedTask);
      toast.success('Task updated');
      setShowTaskDetailDialog(false);
    } catch (error) {
      toast.error(error.message || 'Failed to update task');
    }
  };

  const handleEditTaskOpen = (task) => {
    setEditingTask({ ...task });
    setShowEditTaskDialog(true);
  };

  const handleEditTaskSave = async () => {
    if (!editingTask) return;
    try {
      await updateTask(editingTask.id, {
        title: editingTask.title,
        description: editingTask.description,
        priority: editingTask.priority,
        due_date: editingTask.due_date,
        is_business_critical: editingTask.is_business_critical,
      });
      toast.success(language === 'hi' ? 'टास्क अपडेट हुआ' : 'Task updated');
      setShowEditTaskDialog(false);
      setEditingTask(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateUserInline = async () => {
    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await createUser(newUserData);
      toast.success('User created successfully');
      setNewUserData({ name: '', email: '', password: '', is_admin: false });
      setShowAddUserForm(false);
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([fetchProjects(), fetchTeams()]);
        // Fetch users for admin
        if (currentUser?.is_admin) {
          fetchUsers().catch(console.error);
        }
      } catch (error) {
        toast.error(error.message || t('error'));
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (currentProject) {
      fetchTasks(currentProject.id).catch(console.error);
    }
  }, [currentProject?.id]);

  // Load project members when dialog opens
  const loadProjectMembers = async () => {
    if (!currentProject) return;
    try {
      const members = await fetchProjectMembers(currentProject.id);
      setProjectMembers(members);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId || !currentProject) return;
    try {
      await addProjectMember(currentProject.id, selectedUserId, selectedRole);
      toast.success('Member added');
      loadProjectMembers();
      setSelectedUserId('');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!currentProject) return;
    try {
      await removeProjectMember(currentProject.id, userId);
      toast.success('Member removed');
      loadProjectMembers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleStatusChange = async (status) => {
    if (!currentProject) return;
    try {
      await updateProjectStatus(currentProject.id, status);
      toast.success(`Project marked as ${status}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditProject = async () => {
    if (!editingProject) return;
    try {
      await updateProject(editingProject.id, {
        name: editingProject.name,
        description: editingProject.description,
        is_business_critical: editingProject.is_business_critical,
      });
      toast.success('Project updated');
      setShowEditProjectDialog(false);
      setEditingProject(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error(language === 'hi' ? 'प्रोजेक्ट का नाम आवश्यक है' : 'Project name is required');
      return;
    }

    setIsCreating(true);
    try {
      const result = await createProject(newProject.name, newProject.description, newProject.team_id, newProject.is_business_critical);
      setSuggestedTasks(result.suggested_tasks || []);
      toast.success(language === 'hi' ? 'प्रोजेक्ट बनाया गया' : 'Project created');
      
      if (!result.suggested_tasks?.length) {
        closeDialog();
      }
    } catch (error) {
      toast.error(error.message || t('error'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await deleteProject(projectId);
      toast.success(language === 'hi' ? 'प्रोजेक्ट हटाया गया' : 'Project deleted');
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const closeDialog = () => {
    setShowCreateDialog(false);
    setNewProject({ name: '', description: '', team_id: null, is_business_critical: false });
    setSuggestedTasks([]);
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'green': return 'text-chart-3';
      case 'yellow': return 'text-chart-4';
      case 'red': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getHealthBg = (health) => {
    switch (health) {
      case 'green': return 'bg-chart-3/20';
      case 'yellow': return 'bg-chart-4/20';
      case 'red': return 'bg-destructive/20';
      default: return 'bg-muted';
    }
  };

  // Drag and drop handler
  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const task = tasks.find(t => t.id === draggableId);
    if (!task) return;

    // Optimistic update
    const newStatus = destination.droppableId;
    const newOrder = destination.index;

    try {
      await reorderTask(draggableId, newStatus, newOrder);
      toast.success(language === 'hi' ? 'टास्क अपडेट हुआ' : 'Task updated');
    } catch (error) {
      toast.error(error.message || t('error'));
      // Refetch to revert
      if (currentProject) {
        fetchTasks(currentProject.id);
      }
    }
  };

  const toggleTaskExpand = (taskId) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTasks(newExpanded);
  };

  const handleAddSubtask = async (taskId) => {
    const title = newSubtask[taskId];
    if (!title?.trim()) return;
    
    try {
      await addSubtask(taskId, title);
      setNewSubtask({ ...newSubtask, [taskId]: '' });
      toast.success(language === 'hi' ? 'उप-कार्य जोड़ा गया' : 'Subtask added');
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const handleSubtaskToggle = async (taskId, subtaskId, completed) => {
    try {
      await updateSubtask(taskId, subtaskId, { completed: !completed });
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const projectTasks = currentProject ? tasks.filter(t => t.project_id === currentProject.id) : [];
  
  const columns = [
    { id: 'todo', title: t('todo'), color: 'bg-muted-foreground' },
    { id: 'in_progress', title: t('in_progress'), color: 'bg-chart-4' },
    { id: 'done', title: t('done'), color: 'bg-chart-3' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex" data-testid="projects-page">
      {/* Projects Sidebar */}
      <div className="w-80 border-r border-white/10 p-4 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold">{t('projects')}</h2>
          <Button
            size="icon"
            onClick={() => setShowCreateDialog(true)}
            className="h-8 w-8 glow-primary"
            data-testid="create-project-btn"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto">
          {projects.length === 0 ? (
            <div className="text-center py-8">
              <FolderKanban className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t('no_projects')}</p>
            </div>
          ) : (
            projects.map((project) => (
              <div
                key={project.id}
                onClick={() => setCurrentProject(project)}
                className={`p-3 rounded-xl cursor-pointer transition-all ${
                  currentProject?.id === project.id
                    ? 'bg-primary/20 border border-primary/50'
                    : 'bg-secondary/30 hover:bg-secondary/50 border border-transparent'
                }`}
                data-testid={`project-item-${project.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-lg ${getHealthBg(project.health)} flex items-center justify-center`}>
                      <FolderKanban className={`h-4 w-4 ${getHealthColor(project.health)}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{project.name}</p>
                        {project.is_business_critical && (
                          <span className="business-critical-badge">Critical</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {project.task_counts?.todo || 0} {t('tasks').toLowerCase()}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <MoreVertical className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project.id);
                        }}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Content - Kanban Board */}
      <div className="flex-1 p-6 overflow-x-auto">
        {currentProject ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="font-heading text-2xl font-bold">{currentProject.name}</h1>
                    {currentProject.is_business_critical && (
                      <span className="business-critical-badge">Critical</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      currentProject.status === 'live' ? 'bg-chart-3/20 text-chart-3' :
                      currentProject.status === 'draft' ? 'bg-chart-4/20 text-chart-4' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {currentProject.status || 'draft'}
                    </span>
                  </div>
                  {currentProject.description && (
                    <p className="text-muted-foreground mt-1">{currentProject.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Edit Project */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingProject({ ...currentProject });
                    setShowEditProjectDialog(true);
                  }}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                
                {/* Manage Members */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    loadProjectMembers();
                    if (currentUser?.is_admin) fetchUsers();
                    setShowMembersDialog(true);
                  }}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Members
                </Button>
                
                {/* Project Status - More Prominent */}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/50 border border-white/10">
                  <span className="text-xs text-muted-foreground">Status:</span>
                  <Select
                    value={currentProject.status || 'draft'}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger className={`w-28 h-8 border-0 bg-transparent ${
                      currentProject.status === 'live' ? 'text-chart-3' :
                      currentProject.status === 'draft' ? 'text-chart-4' :
                      currentProject.status === 'completed' ? 'text-primary' :
                      'text-muted-foreground'
                    }`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-chart-4" />
                          Draft
                        </span>
                      </SelectItem>
                      <SelectItem value="live">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-chart-3" />
                          Live
                        </span>
                      </SelectItem>
                      <SelectItem value="completed">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-primary" />
                          Completed
                        </span>
                      </SelectItem>
                      <SelectItem value="archived">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-muted-foreground" />
                          Archived
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button
                  onClick={() => navigate('/app/tasks')}
                  className="glow-primary"
                  data-testid="add-task-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('new_task')}
                </Button>
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2 mb-4">
              <Button
                variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('kanban')}
                className="gap-2"
              >
                <LayoutGrid className="h-4 w-4" />
                Kanban
              </Button>
              <Button
                variant={viewMode === 'gantt' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('gantt')}
                className="gap-2"
              >
                <Calendar className="h-4 w-4" />
                Gantt
              </Button>
              <span className="text-xs text-muted-foreground ml-2">{t('drag_to_reorder')}</span>
            </div>

            {/* Gantt View */}
            {viewMode === 'gantt' && (
              <GanttView 
                projectId={currentProject.id} 
                onTaskClick={(taskId) => {
                  const task = projectTasks.find(t => t.id === taskId);
                  if (task) handleOpenTaskDetail(task);
                }}
              />
            )}

            {/* Kanban Board with Drag and Drop */}
            {viewMode === 'kanban' && (
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="grid grid-cols-3 gap-4 min-w-[900px]">
                {columns.map((column) => {
                  const columnTasks = projectTasks
                    .filter(t => t.status === column.id)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));

                  return (
                    <Droppable droppableId={column.id} key={column.id}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`glass rounded-xl p-4 min-h-[400px] transition-colors ${
                            snapshot.isDraggingOver ? 'bg-primary/10 border-primary/50' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-4">
                            <div className={`h-2 w-2 rounded-full ${column.color}`} />
                            <h3 className="font-medium">{column.title}</h3>
                            <span className="text-sm text-muted-foreground">({columnTasks.length})</span>
                          </div>
                          
                          <div className="space-y-3" style={{ position: 'relative' }}>
                            {columnTasks.map((task, index) => (
                              <Draggable key={task.id} draggableId={task.id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      zIndex: snapshot.isDragging ? 9999 : 'auto',
                                    }}
                                    className={`${snapshot.isDragging ? 'shadow-2xl ring-2 ring-primary opacity-95' : ''}`}
                                  >
                                    <TaskCard
                                      task={task}
                                      dragHandleProps={provided.dragHandleProps}
                                      expanded={expandedTasks.has(task.id)}
                                      onToggleExpand={() => toggleTaskExpand(task.id)}
                                      newSubtask={newSubtask[task.id] || ''}
                                      onNewSubtaskChange={(value) => setNewSubtask({ ...newSubtask, [task.id]: value })}
                                      onAddSubtask={() => handleAddSubtask(task.id)}
                                      onSubtaskToggle={handleSubtaskToggle}
                                      onDeleteSubtask={(subtaskId) => deleteSubtask(task.id, subtaskId)}
                                      onEditTask={handleEditTaskOpen}
                                      onOpenDetail={handleOpenTaskDetail}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        </div>
                      )}
                    </Droppable>
                  );
                })}
              </div>
            </DragDropContext>
            )}
          </>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <FolderKanban className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">
                {language === 'hi' ? 'प्रोजेक्ट चुनें' : 'Select a project'}
              </h2>
              <p className="text-muted-foreground">
                {language === 'hi' ? 'बाएं से एक प्रोजेक्ट चुनें या नया बनाएं' : 'Choose a project from the left or create a new one'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="glass border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('new_project')}</DialogTitle>
          </DialogHeader>
          
          {suggestedTasks.length === 0 ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('project_name')}</Label>
                <Input
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  placeholder={language === 'hi' ? 'प्रोजेक्ट का नाम' : 'Project name'}
                  className="bg-secondary/50 border-white/10"
                  data-testid="project-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('project_description')}</Label>
                <Textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  placeholder={language === 'hi' ? 'विवरण (वैकल्पिक)' : 'Description (optional)'}
                  className="bg-secondary/50 border-white/10"
                  data-testid="project-desc-input"
                />
              </div>
              {teams.length > 0 && (
                <div className="space-y-2">
                  <Label>{t('teams')}</Label>
                  <Select
                    value={newProject.team_id || 'personal'}
                    onValueChange={(value) => setNewProject({ ...newProject, team_id: value === 'personal' ? null : value })}
                  >
                    <SelectTrigger className="bg-secondary/50 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal">{t('personal_projects')}</SelectItem>
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Business Critical Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-[#ff3366] animate-pulse" />
                  <Label className="cursor-pointer">
                    {language === 'hi' ? 'बिज़नेस क्रिटिकल' : 'Business Critical'}
                  </Label>
                </div>
                <Checkbox
                  checked={newProject.is_business_critical}
                  onCheckedChange={(checked) => setNewProject({ ...newProject, is_business_critical: checked })}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-primary">
                <Sparkles className="h-5 w-5" />
                <p className="font-medium">
                  {language === 'hi' ? 'AI सुझाए गए टास्क' : 'AI Suggested Tasks'}
                </p>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {suggestedTasks.map((task, i) => (
                  <div key={i} className="p-3 rounded-lg bg-secondary/30">
                    <p className="font-medium text-sm">{task.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{task.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog}>
              {t('cancel')}
            </Button>
            {suggestedTasks.length === 0 ? (
              <Button 
                onClick={handleCreateProject} 
                disabled={isCreating}
                className="glow-primary"
                data-testid="submit-project-btn"
              >
                {isCreating ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current" />
                    {language === 'hi' ? 'बना रहा है...' : 'Creating...'}
                  </span>
                ) : (
                  t('create_project')
                )}
              </Button>
            ) : (
              <Button onClick={closeDialog} className="glow-primary">
                {t('done')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditProjectDialog} onOpenChange={setShowEditProjectDialog}>
        <DialogContent className="glass border-white/10">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          {editingProject && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Project Name</Label>
                <Input
                  value={editingProject.name}
                  onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                  className="bg-secondary/50 border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingProject.description || ''}
                  onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                  className="bg-secondary/50 border-white/10"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-[#ff3366] animate-pulse" />
                  <Label>Business Critical</Label>
                </div>
                <Checkbox
                  checked={editingProject.is_business_critical}
                  onCheckedChange={(checked) => setEditingProject({ ...editingProject, is_business_critical: checked })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditProjectDialog(false)}>Cancel</Button>
            <Button onClick={handleEditProject}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Members Dialog */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="glass border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Project Members
            </DialogTitle>
          </DialogHeader>
          
          {/* Add Member Form */}
          {currentUser?.is_admin && (
            <div className="space-y-3 p-4 rounded-lg bg-secondary/30">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Add Member</Label>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAddUserForm(!showAddUserForm)}
                  className="text-xs text-primary"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {showAddUserForm ? 'Cancel' : 'New User'}
                </Button>
              </div>
              
              {/* Inline New User Form */}
              {showAddUserForm && (
                <div className="space-y-3 p-3 rounded-lg bg-card/50 border border-white/10">
                  <p className="text-xs text-muted-foreground">Create new user to add to system</p>
                  <Input
                    placeholder="Name"
                    value={newUserData.name}
                    onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                    className="h-8 text-sm"
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                    className="h-8 text-sm"
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                    className="h-8 text-sm"
                  />
                  <Button size="sm" onClick={handleCreateUserInline} className="w-full">
                    <UserPlus className="h-3 w-3 mr-2" />
                    Create User
                  </Button>
                </div>
              )}
              
              <div className="flex gap-2">
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter(u => !projectMembers.find(m => m.user_id === u.id)).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project_lead">Project Lead</SelectItem>
                    <SelectItem value="project_manager">Project Manager</SelectItem>
                    <SelectItem value="contributor">Contributor</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleAddMember} disabled={!selectedUserId}>
                  <UserPlus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Members List */}
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {projectMembers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No members yet</p>
              ) : (
                projectMembers.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 rounded-lg bg-card/50">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary/20 text-primary text-xs">
                          {member.user?.name?.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{member.user?.name}</p>
                        <p className="text-xs text-muted-foreground">{member.user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        member.role === 'project_lead' ? 'bg-primary/20 text-primary' :
                        member.role === 'project_manager' ? 'bg-chart-4/20 text-chart-4' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {member.role === 'project_lead' ? 'Lead' : 
                         member.role === 'project_manager' ? 'Manager' : 'Contributor'}
                      </span>
                      {currentUser?.is_admin && member.user_id !== currentUser.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => handleRemoveMember(member.user_id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={showEditTaskDialog} onOpenChange={setShowEditTaskDialog}>
        <DialogContent className="glass border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              {language === 'hi' ? 'टास्क संपादित करें' : 'Edit Task'}
            </DialogTitle>
          </DialogHeader>
          {editingTask && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('title')}</Label>
                <Input
                  value={editingTask.title}
                  onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                  className="bg-secondary/50 border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('description')}</Label>
                <Textarea
                  value={editingTask.description || ''}
                  onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                  className="bg-secondary/50 border-white/10"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('priority')}</Label>
                  <Select
                    value={editingTask.priority || 'medium'}
                    onValueChange={(v) => setEditingTask({ ...editingTask, priority: v })}
                  >
                    <SelectTrigger className="bg-secondary/50 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">{t('low')}</SelectItem>
                      <SelectItem value="medium">{t('medium')}</SelectItem>
                      <SelectItem value="high">{t('high')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('due_date')}</Label>
                  <Input
                    type="date"
                    value={editingTask.due_date ? editingTask.due_date.split('T')[0] : ''}
                    onChange={(e) => setEditingTask({ ...editingTask, due_date: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <Label>{language === 'hi' ? 'बिज़नेस क्रिटिकल' : 'Business Critical'}</Label>
                </div>
                <Checkbox
                  checked={editingTask.is_business_critical}
                  onCheckedChange={(checked) => setEditingTask({ ...editingTask, is_business_critical: checked })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditTaskDialog(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleEditTaskSave}>
              {language === 'hi' ? 'सहेजें' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog (Comments/Attachments) */}
      <TaskDetailDialog
        task={selectedTaskForDetail}
        open={showTaskDetailDialog}
        onOpenChange={setShowTaskDetailDialog}
        onSave={handleTaskDetailSave}
      />
    </div>
  );
};

// Task Card Component with Subtasks
const TaskCard = ({ 
  task, 
  dragHandleProps, 
  expanded, 
  onToggleExpand,
  newSubtask,
  onNewSubtaskChange,
  onAddSubtask,
  onSubtaskToggle,
  onDeleteSubtask,
  onEditTask,
  onOpenDetail
}) => {
  const { updateTask, deleteTask } = useTaskStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateTask(task.id, { status: newStatus });
      toast.success(language === 'hi' ? 'टास्क अपडेट हुआ' : 'Task updated');
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const subtasks = task.subtasks || [];
  const completedSubtasks = subtasks.filter(st => st.completed).length;

  return (
    <motion.div
      layout
      className="p-3 rounded-lg bg-card/50 border border-white/5 hover:border-white/10 transition-colors"
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start gap-2">
        {/* Drag Handle */}
        <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing mt-1">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm">{task.title}</p>
              {task.is_business_critical && (
                <span className="business-critical-badge">!</span>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 -mr-1">
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onOpenDetail?.(task)}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  {language === 'hi' ? 'विवरण देखें' : 'View Details'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEditTask(task)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  {language === 'hi' ? 'संपादित करें' : 'Edit'}
                </DropdownMenuItem>
                {task.status !== 'todo' && (
                  <DropdownMenuItem onClick={() => handleStatusChange('todo')}>
                    {t('todo')}
                  </DropdownMenuItem>
                )}
                {task.status !== 'in_progress' && (
                  <DropdownMenuItem onClick={() => handleStatusChange('in_progress')}>
                    {t('in_progress')}
                  </DropdownMenuItem>
                )}
                {task.status !== 'done' && (
                  <DropdownMenuItem onClick={() => handleStatusChange('done')}>
                    {t('done')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={async () => {
                    await deleteTask(task.id);
                    toast.success(language === 'hi' ? 'टास्क हटाया गया' : 'Task deleted');
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {task.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.description}</p>
          )}
          
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              task.priority === 'high' ? 'bg-destructive/20 text-destructive' :
              task.priority === 'medium' ? 'bg-chart-4/20 text-chart-4' :
              'bg-muted text-muted-foreground'
            }`}>
              {t(task.priority)}
            </span>
            {task.due_date && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {new Date(task.due_date).toLocaleDateString()}
              </span>
            )}
            {subtasks.length > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                {completedSubtasks}/{subtasks.length}
              </span>
            )}
          </div>

          {/* Subtasks Section */}
          {(subtasks.length > 0 || expanded) && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <button
                onClick={onToggleExpand}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {t('subtasks')} ({subtasks.length})
              </button>
              
              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-2 mt-2">
                      {subtasks.map((subtask) => (
                        <div key={subtask.id} className="flex items-center gap-2 group">
                          <Checkbox
                            checked={subtask.completed}
                            onCheckedChange={() => onSubtaskToggle(task.id, subtask.id, subtask.completed)}
                            className="h-3.5 w-3.5"
                          />
                          <span className={`text-xs flex-1 ${subtask.completed ? 'line-through text-muted-foreground' : ''}`}>
                            {subtask.title}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => onDeleteSubtask(subtask.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                      
                      {/* Add subtask input */}
                      <div className="flex items-center gap-2">
                        <Input
                          value={newSubtask}
                          onChange={(e) => onNewSubtaskChange(e.target.value)}
                          placeholder={t('add_subtask')}
                          className="h-7 text-xs bg-secondary/30 border-white/5"
                          onKeyDown={(e) => e.key === 'Enter' && onAddSubtask()}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={onAddSubtask}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectsPage;
